import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
} from "react";
import { StoreApi, createStore, useStore } from "zustand";

// components
import { toast, ToastContainer } from "react-toastify";

// types
import {
  IOrganizations,
  TOrganizationVerificationStatus,
} from "../../setup/types/Organisation";
import {
  deleteOrganizationAPI,
  getAllOrganizations,
  getOrganization,
  rejectOrganization,
  verifyOrganization,
} from "../../setup/api/services/organization";

interface OrganizationStore {
  loading: boolean;
  page: number;
  pageCount: number;
  organizations?: IOrganizations[];
  selectedOrganisation?: IOrganizations;
  verificationStatusFilter?:
    | TOrganizationVerificationStatus
    | TOrganizationVerificationStatus[];
  searchValue: string;
  setLoading: (value: boolean) => void;
  buttonLoading: boolean;
  setButtonLoading: (value: boolean) => void;
  setPage: (value: number) => void;
  setPageCount: (value: number) => void;
  setOrganizations: (organizations: IOrganizations[]) => void;
  setSelectedOrganization: (organization?: IOrganizations) => void;
  setVerificationStatusFilter: (
    value?: TOrganizationVerificationStatus | TOrganizationVerificationStatus[]
  ) => void;
  searchOrganisation: (value: string) => void;
  deleteOrganisation: (value: string) => void;
  fetchOrganization: (code: string) => void;
  fetchOrganizations: () => void;
  verifyOrganisation: (code: string) => Promise<boolean>;
  rejectOrganisation: (code: string, reason: string) => Promise<void>;
}

const OrganisationContext = createContext<
  StoreApi<OrganizationStore> | undefined
>(undefined);

type OrganisationProviderProps = PropsWithChildren;

const OrganstionProvider = ({ children }: OrganisationProviderProps) => {
  const [store] = useState(() =>
    createStore<OrganizationStore>((set) => ({
      organizations: undefined,
      selectedOrganisation: undefined,
      loading: true,
      buttonLoading: false,
      page: 1,
      pageCount: 1,
      verificationStatusFilter: undefined,
      searchValue: "",
      setLoading: (value: boolean) => {
        set({ loading: value });
      },
      setPage: (page: number) => {
        set({ page });
      },
      setPageCount: (value: number) => {
        set({ pageCount: value });
      },
      setSelectedOrganization: (organization?: IOrganizations) => {
        set({ selectedOrganisation: organization ? organization : undefined });
      },
      setOrganizations: (organizations) => {
        set({ organizations: organizations });
      },
      setVerificationStatusFilter: (
        value?: TOrganizationVerificationStatus | TOrganizationVerificationStatus[]
      ) => {
        set({ verificationStatusFilter: value });
      },
      searchOrganisation: (searchValue) => {
        set({ searchValue });
        fetchOrganizationsList(
          searchValue,
          store.getState().verificationStatusFilter
        );
      },
      deleteOrganisation: (code: string) => {
        deleteOrganisation(code);
      },
      fetchOrganization: (code: string) => {
        fetchOrganization(code);
      },
      setButtonLoading: (value: boolean) => {
        set({ buttonLoading: value });
      },
      fetchOrganizations: () => {
        fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
      },
      verifyOrganisation: (code: string) => verifyOrganisation(code),
      rejectOrganisation: (code: string, reason: string) =>
        rejectOrganisation(code, reason),
    }))
  );

  const pageSize = 10;

  const fetchOrganizationsList = useCallback(
    async (
      searchValue: string,
      verificationStatus?:
        | TOrganizationVerificationStatus
        | TOrganizationVerificationStatus[]
    ) => {
      store.getState().setLoading(true);
      try {
        const response = await getAllOrganizations(
          store?.getState().page - 1,
          pageSize,
          searchValue,
          verificationStatus
        );
        store.getState().setOrganizations(response.data.content);
        store.getState().setPageCount(response.data.totalPages);
      } catch (error) {
        console.error;
      } finally {
        store.getState().setLoading(false);
      }
    },
    [store]
  );

  const verifyOrganisation = useCallback(
    async (code: string) => {
      store.getState().setButtonLoading(true);
      try {
        await verifyOrganization(code);
        await fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
        return true;
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
        return false;
      } finally {
        store.getState().setButtonLoading(false);
      }
    },
    [fetchOrganizationsList, store]
  );

  const rejectOrganisation = useCallback(
    async (code: string, reason: string) => {
      store.getState().setButtonLoading(true);
      try {
        await rejectOrganization(code, reason);
        await fetchOrganizationsList(
          store.getState().searchValue,
          store.getState().verificationStatusFilter
        );
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
      } finally {
        store.getState().setButtonLoading(false);
      }
    },
    [fetchOrganizationsList, store]
  );

  const refreshPage = useCallback(() => {
    window?.location?.reload();
  }, []);

  const deleteOrganisation = useCallback(
    async (code: string) => {
      store.getState().setButtonLoading(true);
      try {
        await deleteOrganizationAPI(code, { code: code });
        refreshPage();
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
        store.getState().setButtonLoading(false);
      }
    },
    [refreshPage, store]
  );

  const fetchOrganization = useCallback(
    async (code: string) => {
      store.getState().setLoading(true);
      try {
        const response = await getOrganization(code);
        store.getState().setSelectedOrganization(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        store.getState().setLoading(false);
      }
    },
    [store]
  );

  return (
    <OrganisationContext.Provider value={store}>
      {children}
      {/* ─── Toast Container ───────────────────────────── */}
      <ToastContainer />
    </OrganisationContext.Provider>
  );
};

export default OrganstionProvider;

export function useOrganisationStore<T>(
  selector: (state: OrganizationStore) => T
) {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error("Organisation Provider is missing");
  }

  return useStore(context, selector);
}
